import { ProductsResponse, Product } from '@services/product';
import { AuthorizationService } from '@services/auth';
import { handleServiceError } from '@utils';
import { SearchEngineBFFClient } from './bffClient';
import { SEARCH_ENGINE } from '@constants';
import {
  ConversationalProductsSearchResponse,
  ConversationalProductsSearchParams,
  ImageToQueryResponse
} from './interfaces';

export class SearchService extends AuthorizationService {
  static readonly serviceName: string = 'SearchService';
  private searchClient: SearchEngineBFFClient;

  constructor() {
    super();
    this.searchClient = new SearchEngineBFFClient(SEARCH_ENGINE);
  }

  public async getProductsFullTextSearch(
    searchQuery: string,
    offset = 0,
    limit = 20,
    facets: string[] = [],
    selectedTerms: Record<string, string[]> = {},
    sort?: string,
    selectedTenantKey?: string,
    conversationId?: string
  ): Promise<ProductsResponse | null> {
    try {
      return await this.searchClient.getProductsSearch({
        sort,
        query: searchQuery,
        offset,
        limit,
        facets,
        selectedTerms,
        selectedTenantKey,
        conversationId
      });

    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  /**
   *
   * @param categoryId
   * @param offset
   * @param facets
   * @param selectedTerms
   * @param sort
   * @param categoryTree - used for MXP search to lookup price filter,
   * is not required for Algolia as price facet is coming from different endpoint
   * in CategoryService.getCategoryPriceFacets
   */
  public async getProductsByCategoryId(
    categoryId: string,
    offset = 0,
    facets: string[] = [],
    selectedTerms: Record<string, string[]> = {},
    sort?: string,
    categoryTree = '',
    selectedTenantKey?: string
  ): Promise<ProductsResponse | null> {
    try {

      return await this.searchClient.getProductsSearch({
        categoryId,
        offset,
        facets,
        selectedTerms,
        sort,
        categoryTree,
        selectedTenantKey
      });

    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getQuerySuggestions(searchKeyword: string): Promise<string[]> {
    try {
      return await this.searchClient.getQuerySuggestions(searchKeyword);
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }

  public async getProductSuggestions(searchKeyword: string): Promise<Product[]> {
    try {
      return await this.searchClient.getProductSuggestions(searchKeyword);
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }

  public async getAllProducts(offset = 0, sort?: string, selectedTenantKey?: string): Promise<ProductsResponse | null> {
    try {
      return await this.searchClient.getProductsSearch({
        offset,
        sort,
        selectedTenantKey
      });
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getProductsByDiscountId(
    discountId: string,
    offset = 0,
    sort?: string
  ): Promise<ProductsResponse | null> {
    try {
      return await this.searchClient.getProductsSearch({
        discountId,
        offset,
        sort
      });
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getProductsByIDs(ids : string[], offset = 0, selectedTenantKey?: string): Promise<ProductsResponse | null> {
    try {
      return await this.searchClient.getProductsSearch({
        productIDs: ids,
        offset,
        selectedTenantKey
      });
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async sendConversionEvent(productIDs: string[], userId?: string | null) :Promise<void> {
    try {
      await this.searchClient.sendConversionEvent(productIDs, userId ?? this.getAccessToken());
    } catch (err) {
      handleServiceError(err, { showSnackbar: false });
    }

    return;
  }

  public async sendViewEvent(productIDs:string[], userId?: string | null) :Promise<void> {
    try {
      await this.searchClient.sendViewEvent(productIDs, userId ?? this.getAccessToken());

    } catch (err) {
      handleServiceError(err, { showSnackbar: false });
    }

    return;
  }

  public async sendClickEvent(productIDs: string[], userId: string | null | undefined, positions: number[]) :Promise<void> {
    try {
      await this.searchClient.sendClickEvent(productIDs, positions, userId ?? this.getAccessToken());
    } catch (err) {
      handleServiceError(err, { showSnackbar: false });
    }

    return;
  }

  public async getSuggestions(searchKeyword: string): Promise<{
  querySuggestions: string[];
  productSuggestions: Product[];
  userQuestions: { question: string }[];
}> {
    try {
      return await this.searchClient.getSuggestions(searchKeyword);
    } catch (err) {
      handleServiceError(err);

      return { querySuggestions: [], productSuggestions: [], userQuestions: [] };
    }
  }

  public async getConversationStarterExamples(): Promise<string[]> {
    try {
      return await this.searchClient.getConversationStarterExamples();
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }

  /**
   * Unified conversational search method that handles both initial searches and follow-up interactions
   * @param params - Parameters object containing search configuration
   * @param params.query - The search query or follow-up text
   * @param params.conversationId - Optional conversation ID for follow-up searches
   * @param params.refinedQueries - Optional refined queries from previous interactions
   * @param params.contents - Previous conversation messages for context
   * @param params.answer - Optional selected answer from suggested options
   * @param params.textAnswer - Optional free-form text answer
   * @param params.filters - Applied filters for the search
   * @param params.offset - Offset for pagination
   * @param params.limit - Limit for pagination
   * @param params.previousSuccessfulSearchRequest - Previous successful search request context
   */
  public async getConversationalProductsSearch(
    params: ConversationalProductsSearchParams
  ): Promise<ConversationalProductsSearchResponse | null> {
    try {
      return await this.searchClient.getConversationalProductsSearch(params);
    } catch (err) {
      handleServiceError(err);

      throw err;
    }
  }

  /**
   * Converts an uploaded image to a search query using AI
   * @param image - The uploaded image file
   * @param userNotes - Optional user notes about the image
   * @returns Promise resolving to image-to-query response or null on error
   */
  public async imageToQuery(image: File, userNotes?: string): Promise<ImageToQueryResponse | null> {
    try {
      return await this.searchClient.imageToQuery({
        image,
        userNotes
      });
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }
}
